import { FC, ReactNode, useId, useState } from "react";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import { useTranslation } from "../../../hooks/useTranslation";

interface Props {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  type?: string;
  placeholder?: string;
  name?: string;
  autoComplete?: string;
  error?: string | false | null;
  /** Extra action shown right under the error, e.g. a "Sign in instead" link. */
  errorAction?: ReactNode;
  /** Adds a show/hide toggle that switches the input between `type` and "text". */
  revealable?: boolean;
  /** Put the cursor here when the page opens (the sign-in page, once the e-mail is already filled in). */
  autoFocus?: boolean;
}

/**
 * The field used by every AuthShell page — same visual language as
 * OnboardingPreview's `Field.tsx` (obp-field), plus a password show/hide
 * toggle that component doesn't need. See AuthShell/styles.scss for why this
 * isn't literally the same component.
 */
export const AuthField: FC<Props> = ({
  label, value, onChange, onBlur, type = "text", placeholder, name, autoComplete,
  error, errorAction, revealable, autoFocus,
}) => {
  const { p } = useTranslation("first_page");
  const [revealed, setRevealed] = useState(false);
  const errorId = `${useId()}-error`;
  const resolvedType = revealable ? (revealed ? "text" : "password") : type;

  return (
    <div className="authshell-field">
      <label className="authshell-field__label" htmlFor={errorId + "-input"}>{label}</label>
      <div className="authshell-field__wrap">
        <input
          id={errorId + "-input"}
          className={
            "authshell-field__input" +
            (revealable ? " authshell-field__input--eye" : "") +
            (error ? " authshell-field__input--error" : "")
          }
          type={resolvedType}
          value={value}
          placeholder={placeholder}
          name={name}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          aria-describedby={error ? errorId : undefined}
          onChange={onChange}
          onBlur={onBlur}
        />
        {revealable && (
          <button
            type="button"
            className="authshell-field__eye"
            onClick={() => setRevealed((r) => !r)}
            aria-label={revealed ? p("hide_password") : p("show_password")}
            tabIndex={-1}
          >
            {revealed ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
          </button>
        )}
      </div>
      {error && (
        <p className="authshell-field__error" id={errorId} role="alert">{error}</p>
      )}
      {error && errorAction}
    </div>
  );
};
